import { NormalizedStar } from 'api/galaxyApi';
import { GalaxyConfig, StarData } from 'types/galaxy';

import { pnlToColor } from './colorUtils';

const SIZE_MIN = 0.4;
const SIZE_MAX = 10.0;

export const processStarsFromApi = (
  apiStars: NormalizedStar[],
  config: GalaxyConfig
): StarData[] => {
  const starCount = apiStars.length;

  // Pre-pass: find max |PnL| for dynamic scaling
  let maxAbsPnl = 0;
  for (const star of apiStars) {
    const absPnl = Math.abs(star.pnl);
    if (absPnl > maxAbsPnl) maxAbsPnl = absPnl;
  }
  maxAbsPnl = Math.max(maxAbsPnl, 1);
  const logMax = Math.log(1 + maxAbsPnl);

  return apiStars.map((star, index) => {
    // Color: log-scale HSL based on PnL
    const color = pnlToColor(star.pnl, logMax);

    // Size: log scale of |PnL|, 25x ratio (0.4 → 10.0)
    const pnlNorm = Math.log(1 + Math.abs(star.pnl)) / logMax;
    const size = SIZE_MIN + (SIZE_MAX - SIZE_MIN) * pnlNorm;

    // Position: spiral with PnL-based distance (profitable = closer to center)
    const pnlNormalized = Math.max(-1, Math.min(1, star.pnl / maxAbsPnl));
    const tBase = index / starCount;
    const t = Math.max(0.05, tBase * (1 - pnlNormalized * 0.3));
    const distance = config.coreSize + t * (config.galaxyRadius - config.coreSize);

    const armIndex = Math.floor(Math.random() * config.arms);
    const armAngle = (armIndex / config.arms) * Math.PI * 2;
    const spiralAngle = armAngle + t * config.armCurve + (Math.random() - 0.5) * 0.6;
    const spread = 2 + Math.random() * 4;

    const x = Math.cos(spiralAngle) * distance + (Math.random() - 0.5) * spread;
    const y = (Math.random() - 0.5) * config.thickness * (1 - t * 0.5);
    const z = Math.sin(spiralAngle) * distance + (Math.random() - 0.5) * spread;

    // 0.0015 matches original app.js line 2571 (original runs at 60fps, engine at 30fps,
    // but rotationSpeed is multiplied by dt*60 in animateCurrentMode so speeds match)
    const baseSpeed = 0.0015;
    const rotationSpeed = baseSpeed / Math.sqrt(0.3 + distance / config.galaxyRadius);

    return {
      wallet: star.wallet,
      pnl: star.pnl,
      roi: star.roi,
      f_roi: star.f_roi,
      totalTrades: star.totalTrades,
      mind: star.mind,
      position: { x, y, z },
      spiralData: {
        distance,
        angle: spiralAngle,
        baseY: y,
        rotationSpeed,
        origAngle: spiralAngle,
        origDistance: distance,
        origBaseY: y,
        origRotationSpeed: rotationSpeed,
      },
      color,
      size,
    };
  });
};
